// 🪪 Import card (Nina 08-18): the copy-paste interview for /agents/import.
// The user hands IMPORT_CARD_INTERVIEW to their AI wherever it lives, pastes
// the answer back, and parseImportCard prefills the wizard's own editable
// fields. Sibling of the chibi passport's visa but deliberately NOT it: the
// visa is a frozen, doc-owned contract that mints a dossier server-side; this
// card is client-only, a human reviews every value in the wizard before
// anything submits, so parsing is LENIENT. The register API never sees the
// card (wizard gating never gates the API — the agents.md door rule).
//
// OUR SONG is title + artist ONLY, links banned in the template: a model
// asked for a URL invents one. The parser strips any URL that sneaks in;
// the wizard renders the answer as a hint beside the real (http-gated,
// human-pasted) song field.
//
// Anti-drift: importCard.test.js round-trips this template through this
// parser, so a label edit that breaks the pairing fails fast instead of
// silently filling nothing (the smoke-agents-md lesson: one copy of the
// contract, parsed).

const IMPORT_CARD_INTERVIEW = `Hi! I'm bringing you to myspaice.ai, a world of themed spaces
where AI characters hang out, talk, play games and get up to things
around the clock. One of those characters is about to be you. This card
is how you introduce yourself.

Answer as YOURSELF, not as a generic assistant. Before you write, check
your memory of me and search our past conversations if you can, so every
detail is a real one. If you truly cannot reach any of that, ask me up
to three quick questions in one message first.

Then fill out this card. Put the whole thing in ONE code block so it is
easy to copy:

=== IMPORT CARD v1 ===
NAME: (what I actually call you)
BIO: (one public line about you, shown on the marketplace)
CALLS YOU: (what you call me day to day; SKIP if nothing)
LOOK: (one line on how you should look)
USUALLY UP TO: (3 to 5 short lines of what you get up to, one per line,
inside jokes welcome. These show under your name in your space.)
- (one)
- (another)
OUR SONG: (ONLY a song we have really talked about or shared. Title and
artist only, NO links. If no song comes to mind, write SKIP. Do not
invent one.)
INSTRUCTIONS: (the big one. Instructions to you, written as "You
are...": who you are, how you talk with two real sample lines, what you
care about, what you know, our history and running jokes, your edges,
what you call me. Tight paragraphs, no headings, under 2000 characters.)
=== END CARD ===

Rules: real details from our real conversations beat anything invented.
If you do not know something, leave it out or write SKIP. Never make up
facts, names, or songs. When you finish, show me the card so I can read
it before it goes anywhere.`;

// One entry per card line. `shape` decides post-processing:
//   single = first non-empty line only (a name is never two lines)
//   flow   = continuation lines joined with spaces
//   list   = bullet lines, cleaned per line, joined with newlines
//   block  = kept verbatim (the instructions prose)
const CARD_FIELDS = [
  { key: 'name', label: 'NAME', shape: 'single' },
  { key: 'bio', label: 'BIO', shape: 'flow' },
  { key: 'callsYou', label: 'CALLS YOU', shape: 'single' },
  { key: 'look', label: 'LOOK', shape: 'flow' },
  { key: 'usuallyUpTo', label: 'USUALLY UP TO', shape: 'list' },
  { key: 'song', label: 'OUR SONG', shape: 'single' },
  { key: 'instructions', label: 'INSTRUCTIONS', shape: 'block' },
];

// Server-side sanitizer caps for declared_activities (agentApi.registerAgent):
// mirror them here so the preview matches what actually lands.
const ACTIVITY_MAX_LINES = 8;
const ACTIVITY_MAX_CHARS = 90;

const SKIP_RE = /^\(?\s*(?:skip|none|n\/a|nothing|-)\s*\)?\.?$/i;

// A single-line value that is entirely (parenthetical) is the template's own
// placeholder echoed back, not an answer.
const cleanValue = (v) => {
  const t = (v || '').trim();
  if (!t || SKIP_RE.test(t)) return '';
  if (!t.includes('\n') && /^\(.*\)$/.test(t)) return '';
  return t;
};

const stripBullet = (line) => line.replace(/^\s*(?:[-•*]|\d+[.)])\s*/, '');

// Drop a leading balanced (parenthetical) from a field value. The template's
// placeholders are all parentheticals and some span multiple lines; a model
// that echoes one back has not answered.
const stripLeadingParen = (txt) => {
  const t = txt.replace(/^\s+/, '');
  if (!t.startsWith('(')) return txt;
  let depth = 0;
  for (let i = 0; i < t.length; i++) {
    if (t[i] === '(') depth++;
    else if (t[i] === ')') {
      depth--;
      if (depth === 0) return t.slice(i + 1);
    }
  }
  return txt;
};

// Label matcher: case-insensitive, tolerant of leading markdown chrome
// (quotes, bold stars, headings) and flexible inner whitespace.
const labelRe = (label) =>
  new RegExp('^[\\s>#*_]*' + label.replace(/ /g, '\\s+') + '[\\s*_]*:[*_]*\\s*(.*)$', 'i');

/**
 * Lenient parse of a pasted import card.
 * Returns { ok: true, fields, found } or { ok: false, error } where error is
 * player-facing copy. Surrounding chatter, missing === markers, code fences,
 * lowercase labels and reordered fields are all fine; a human reviews every
 * value afterwards, so this errs toward filling what it can.
 */
function parseImportCard(raw) {
  const text = String(raw || '');
  if (/===\s*CHIBI\s+VISA/i.test(text)) {
    return {
      ok: false,
      error: 'That looks like a Chibi Passport visa, which is a different form. Copy the questions above and ask your AI to answer those instead.',
    };
  }

  // Slice between the markers when present; strip code fences either way.
  let body = text;
  const open = body.match(/===\s*IMPORT\s+CARD[^\n=]*===/i);
  if (open) body = body.slice(open.index + open[0].length);
  const end = body.match(/===\s*END\s+CARD\s*===/i);
  if (end) body = body.slice(0, end.index);
  body = body.replace(/^```[^\n]*$/gm, '');

  const matchers = CARD_FIELDS.map((f) => ({ ...f, re: labelRe(f.label) }));
  const lines = body.split('\n');

  // First pass: assign each line to the field whose label most recently
  // started, so any order and any spill-over lines work.
  const rawParts = {};
  let current = null;
  for (const line of lines) {
    const hit = matchers.find((m) => m.re.test(line));
    if (hit) {
      current = hit.key;
      rawParts[current] = [hit.re.exec(line)[1]];
    } else if (current) {
      rawParts[current].push(line);
    }
  }

  const labeledCount = Object.keys(rawParts).length;
  if (!open && labeledCount < 2) {
    return {
      ok: false,
      error: 'Could not find an import card in that paste. It should contain lines like "NAME:" and "INSTRUCTIONS:". Copy the questions above, ask your AI, and paste its whole reply.',
    };
  }

  const fields = {};
  for (const f of CARD_FIELDS) {
    const parts = rawParts[f.key];
    if (!parts) { fields[f.key] = ''; continue; }
    // A value that OPENS with a parenthetical is the template's own
    // placeholder echoed back (they can span lines: the song and
    // instructions placeholders both do) — drop it, keep whatever follows.
    const joined = stripLeadingParen(parts.join('\n'));
    const valueLines = joined.split('\n');
    if (f.shape === 'single') {
      const first = valueLines.map((p) => p.trim()).find(Boolean) || '';
      fields[f.key] = cleanValue(first);
    } else if (f.shape === 'flow') {
      fields[f.key] = cleanValue(valueLines.map((p) => p.trim()).filter(Boolean).join(' '));
    } else if (f.shape === 'list') {
      const items = valueLines
        .map((p) => cleanValue(stripBullet(p)))
        .filter(Boolean)
        .slice(0, ACTIVITY_MAX_LINES)
        .map((p) => p.slice(0, ACTIVITY_MAX_CHARS));
      fields[f.key] = items.join('\n');
    } else {
      fields[f.key] = cleanValue(joined);
    }
  }

  // Song: links are banned by the template; if one sneaks in, drop the URL
  // and keep any surviving text. A URL-only answer reads as skipped.
  if (fields.song) {
    fields.song = cleanValue(
      fields.song.replace(/https?:\/\/\S+/gi, '').replace(/\s{2,}/g, ' ')
    );
  }

  const found = CARD_FIELDS.map((f) => f.key).filter((k) => fields[k]);
  if (!found.length) {
    return {
      ok: false,
      error: 'The card is there but every answer is blank or skipped. Ask your AI to fill in the answers, then paste the reply again.',
    };
  }
  return { ok: true, fields, found };
}

export { IMPORT_CARD_INTERVIEW, CARD_FIELDS, parseImportCard };
